import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

export type ToasterType = 'success' | 'warning' | 'danger' | 'info';

export interface IToasterMessage {
  id: string;
  type: ToasterType;
  message: string;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToasterService {
  private toasterSubject = new Subject<IToasterMessage>();
  public toaster$ = this.toasterSubject.asObservable();

  /**
   * Show a success toaster message
   * @param message The message to display
   * @param duration Duration in milliseconds (default: 3000)
   */
  showSuccess(message: string, duration: number = 3000): void {
    this.show('success', message, duration);
  }

  /**
   * Show a warning toaster message
   * @param message The message to display
   * @param duration Duration in milliseconds (default: 3000)
   */
  showWarning(message: string, duration: number = 3000): void {
    this.show('warning', message, duration);
  }

  /**
   * Show a danger/error toaster message
   * @param message The message to display
   * @param duration Duration in milliseconds (default: 3000)
   */
  showDanger(message: string, duration: number = 3000): void {
    this.show('danger', message, duration);
  }

  /**
   * Show an info toaster message
   * @param message The message to display
   * @param duration Duration in milliseconds (default: 3000)
   */
  showInfo(message: string, duration: number = 3000): void {
    this.show('info', message, duration);
  }

  /**
   * Show a toaster message with custom type
   * @param type The type of message
   * @param message The message to display
   * @param duration Duration in milliseconds (default: 3000)
   */
  private show(type: ToasterType, message: string, duration: number = 3000): void {
    const id = this.generateId();
    const toasterMessage: IToasterMessage = {
      id,
      type,
      message,
      duration
    };
    this.toasterSubject.next(toasterMessage);
  }

  private generateId(): string {
    return `toaster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
